import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Edit, Trash2, X, PlusCircle, Save } from 'lucide-react';

const Items = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', default_rate: 0 });

    const fetchItems = async () => {
        try {
            const res = await api.get('/items');
            setItems(res.data.data);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch items', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        console.log('Saving Item:', { editingId, formData });
        try {
            if (editingId) {
                console.log('Sending PUT request to:', `/items/${editingId}`);
                await api.put(`/items/${editingId}`, formData);
                alert('Item updated successfully!');
                setEditingId(null);
            } else {
                console.log('Sending POST request to:', '/items');
                await api.post('/items', formData);
                alert('Item added successfully!');
            }
            setFormData({ name: '', description: '', default_rate: 0 });
            fetchItems();
        } catch (err) {
            console.error('Failed to save item. Full Error:', err);
            alert('Error saving item');
        }
    };

    const handleEdit = (item) => {
        const id = item.id || item._id;
        console.log('Editing Item:', { id, item });
        setEditingId(id);
        setFormData({
            name: item.name,
            description: item.description || '',
            default_rate: item.default_rate
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ name: '', description: '', default_rate: 0 });
    };

    const handleDelete = async (id) => {
        console.log('Deleting Item ID:', id);
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        try {
            const res = await api.delete(`/items/${id}`);
            console.log('Delete Response:', res.data);
            if (res.data.status === 'success') {
                fetchItems();
            }
        } catch (err) {
            console.error('Failed to delete item. Full Error:', err);
            alert('Error deleting item');
        }
    };

    return (
        <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
            <h1 className="text-2xl md:text-[2.2rem] font-[900] text-primary mb-10 tracking-tight">Particulars Base</h1>

            <div className={`bg-white rounded-2xl border-2 ${editingId ? 'border-amber-200' : 'border-slate-200'} mb-10 overflow-hidden shadow-sm transition-all`}>
                <div className={`section-header ${editingId ? 'bg-amber-50/50' : ''}`}>
                    <div className="flex items-center gap-3">
                        {editingId ? <Edit size={20} className="text-amber-600" /> : <PlusCircle size={20} className="text-primary" />}
                        <h3>{editingId ? 'Modify Particular Record' : 'Register New Particular'}</h3>
                    </div>
                    {editingId && (
                        <button
                            onClick={handleCancelEdit}
                            className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-lg transition-all"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>
                <div className="bg-slate-50/10">
                    <form onSubmit={handleSave} className="grid-partitioned grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-end">
                        <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                            <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Item Name</label>
                            <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Server Maintenance" className="input-field" />
                        </div>
                        <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                            <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Detailed Description</label>
                            <input name="description" value={formData.description} onChange={handleChange} placeholder="e.g. Annual service package" className="input-field" />
                        </div>
                        <div className="p-8 group hover:bg-slate-50/50 transition-colors">
                            <label className="block text-[11px] font-[800] text-slate-400 mb-3 uppercase tracking-[0.1em]">Default Rate (AED)</label>
                            <input name="default_rate" type="number" step="0.01" value={formData.default_rate} onChange={handleChange} className="input-field font-bold text-primary" />
                        </div>
                        <div className="p-8 flex gap-3">
                            {editingId && (
                                <button type="button" onClick={handleCancelEdit} className="p-3.5 px-6 rounded-lg font-bold text-slate-500 hover:bg-white transition-all border-2 border-transparent">
                                    <X size={20} />
                                </button>
                            )}
                            <button type="submit" className={editingId ? "bg-amber-600 text-white font-bold py-3.5 px-8 rounded-lg hover:bg-amber-700 transition-all shadow-md w-full flex items-center justify-center gap-2" : "btn-primary w-full !py-3.5 shadow-md flex items-center justify-center gap-2"}>
                                {editingId ? <><Save size={20} /> Update</> : <><PlusCircle size={20} /> Add to Base</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="bg-white rounded-2xl p-1 md:p-1 border-2 border-slate-200 overflow-hidden shadow-sm">
                <table className="data-table !border-none !rounded-none">
                    <thead>
                        <tr>
                            <th className="!py-6">Particular Account Name</th>
                            <th className="!py-6">Service / Item Description</th>
                            <th className="!py-6 text-right">Standard Rate (AED)</th>
                            <th className="text-center !border-r-0">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="4" className="p-12 text-center font-bold text-slate-400 italic">Accessing particulars database...</td></tr> :
                            items.map(i => (
                                <tr key={i._id || i.id} className={`hover:bg-slate-50/80 transition-all ${editingId === (i.id || i._id) ? 'bg-amber-50/30' : ''}`}>
                                    <td className="!py-6" data-label="Name">
                                        <span className="font-[800] text-primary">{i.name}</span>
                                    </td>
                                    <td className="!py-6 text-slate-500 font-medium" data-label="Description">{i.description}</td>
                                    <td className="!py-6 text-right" data-label="Rate">
                                        <span className="font-[900] text-slate-900">AED {parseFloat(i.default_rate || 0).toFixed(2)}</span>
                                    </td>
                                    <td className="!py-6 !border-r-0" data-label="Actions">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                onClick={() => handleEdit(i)}
                                                className="p-2 hover:bg-primary hover:text-white rounded-lg text-primary transition-all border border-slate-100 hover:border-primary"
                                                title="Edit"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(i.id || i._id)}
                                                className="p-2 hover:bg-red-600 hover:text-white rounded-lg text-red-500 transition-all border border-slate-100 hover:border-red-600"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        }
                        {!loading && items.length === 0 && (
                            <tr><td colSpan="4" className="text-center p-16 text-slate-400 font-bold italic">No particular records found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Items;
